import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { Button, Flex, Input, Modal, Paragraph, Select, Text, message } from '@nula/core-ui'
import { ApiError } from '@/lib/http/client'
import { transferOwnership } from '@/lib/workspace/api/api'
import { useWorkspaceStore } from '@/stores/use-workspace-store'
import type { WorkspaceInfo, WorkspaceMember } from '@/lib/workspace/types/types'
import styles from './transfer-ownership-modal.module.css'

interface TransferOwnershipModalProps {
  workspace: WorkspaceInfo
  members: WorkspaceMember[]
  open: boolean
  onClose: () => void
}

export function TransferOwnershipModal({ workspace, members, open, onClose }: TransferOwnershipModalProps) {
  const [targetUserId, setTargetUserId] = useState<string>('')
  const [confirmName, setConfirmName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const refresh = useWorkspaceStore((s) => s.refreshFromServer)

  // Only admins are eligible — server returns 404 if target isn't an admin.
  const eligible = members.filter((m) => m.role === 'admin')

  const canSubmit = targetUserId !== '' && confirmName === workspace.name && !submitting

  const handleTransfer = async () => {
    if (!targetUserId) return
    setSubmitting(true)
    try {
      await transferOwnership(workspace.id, targetUserId)
      await refresh()
      onClose()
    } catch (err: unknown) {
      console.error('[TransferOwnership] failed:', err)
      Sentry.captureException(err)
      if (err instanceof ApiError && err.status === 409) {
        message.error('Another transfer is in progress; please try again.')
      } else {
        message.error(err instanceof ApiError ? err.message : 'Transfer failed.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const footer = (
    <>
      <Button variant="text" onClick={onClose}>Cancel</Button>
      <Button danger disabled={!canSubmit} loading={submitting} onClick={handleTransfer}>
        Transfer ownership
      </Button>
    </>
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Transfer ownership"
      footer={footer}
    >
      <Flex vertical gap="md">
        <Paragraph>
          <Text strong className={styles.warning}>Warning:</Text> this is permanent. After transfer, you'll be an admin. Only an admin can become the new owner; promote a member first if needed.
        </Paragraph>

        <Flex vertical gap="xs">
          <Text type="secondary">New owner</Text>
          <Select
            value={targetUserId}
            label="Select an admin…"
            options={eligible.map((m) => ({
              value: m.userId,
              label: `${m.name ?? m.email} (admin)`,
            }))}
            onChange={(v) => setTargetUserId(v as string)}
          />
        </Flex>

        <Flex vertical gap="xs">
          <Text type="secondary">Type "{workspace.name}" to confirm</Text>
          <Input
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            label={workspace.name}
          />
        </Flex>
      </Flex>
    </Modal>
  )
}

